import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Link,
} from '@mui/material';
import { ContentCopy as CopyIcon, Person as PersonIcon } from '@mui/icons-material';

/**
 * Minimalist installation section component
 */
export default function InstallationSection() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#fafafa',
        py: 3,
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h5"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 3,
            fontWeight: 'bold',
            color: '#333',
          }}
        >
          Get Started
        </Typography>

        <Grid container spacing={2}>
          {/* Left Column: Installation and Usage (2/3 width) */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={1.5} direction="column">
              {/* React Package Installation - Primary */}
              <Grid item>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                    📦 Install
                  </Typography>
                  
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 1.5,
                      borderRadius: 1,
                      mb: 1.5,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      border: '1px solid #ddd',
                    }}
                  >
                    npm install @hashatar/react
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard('npm install @hashatar/react')}
                    sx={{
                      borderColor: '#4caf50',
                      color: '#2e7d32',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    Copy
                  </Button>
                </Paper>
              </Grid>

              {/* Usage Example - React Primary */}
              <Grid item>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                    🚀 Usage
                  </Typography>
                  
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 1.5,
                      borderRadius: 1,
                      mb: 1.5,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      border: '1px solid #ddd',
                      textAlign: 'left',
                    }}
                  >
                    <Typography component="pre" sx={{ fontSize: 'inherit', margin: 0 }}>
{`import { Hashatar } from '@hashatar/react';

function App() {
  return (
    <div style={{ width: 200, height: 200 }}>
      <Hashatar text="user@example.com" />
    </div>
  );
}`}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(`import { Hashatar } from '@hashatar/react';\n\nfunction App() {\n  return (\n    <div style={{ width: 200, height: 200 }}>\n      <Hashatar text="user@example.com" />\n    </div>\n  );\n}`)}
                    sx={{
                      borderColor: '#4caf50',
                      color: '#2e7d32',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    Copy
                  </Button>
                </Paper>
              </Grid>

              {/* Core Package - Secondary Option */}
              <Grid item>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                    🔧 Core JavaScript
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    For vanilla JavaScript or other frameworks
                  </Typography>
                  
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 1.5,
                      borderRadius: 1,
                      mb: 1.5,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      border: '1px solid #ddd',
                      textAlign: 'left',
                    }}
                  >
                    <Typography component="pre" sx={{ fontSize: 'inherit', margin: 0 }}>
{`npm install @hashatar/core

import { generateHashatar } from '@hashatar/core';
const avatar = generateHashatar('hello', { size: 32 });`}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(`npm install @hashatar/core\n\nimport { generateHashatar } from '@hashatar/core';\nconst avatar = generateHashatar('hello', { size: 32 });`)}
                    sx={{
                      borderColor: '#4caf50',
                      color: '#2e7d32',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    Copy
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column: Contact (1/3 width, full height) */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                border: '1px solid #e0e0e0',
                boxShadow: 'none',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                💬 Contact
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Questions or feedback?
              </Typography>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonIcon />}
                component={Link}
                href="https://www.tela.app/id/complex"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: '#4caf50',
                  color: '#2e7d32',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  '&:hover': {
                    borderColor: '#2e7d32',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Tela
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Footer note */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Open source • MIT License • Made with ❤️
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
